import { useState } from "react";
import { Link } from "react-router-dom";

import styles from "./RepositoriesGrid.module.css";
import { useDocumentTitle } from "../../chrome/useDocumentTitle";
import { Button, ButtonLink } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import {
  Menu,
  MenuCheck,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from "../../components/Menu";
import { Pill } from "../../components/Pill";
import { RepoAvatar } from "../../components/RepoAvatar";
import { Segmented } from "../../components/Segmented";
import { useCurrentWorkspace } from "../../data/api/currentWorkspace";
import { useRepositories } from "../../data/api/repositories";
import { Icon } from "../../icons";
import { cx } from "../../lib/cx";
import { paths } from "../../routing/paths";

const ALL_TEAMS = "all";

export const RepositoriesGrid = () => {
  const { workspaceId } = useCurrentWorkspace();
  const [team, setTeam] = useState(ALL_TEAMS);
  const [sort, setSort] = useState<"activity" | "name">("activity");
  useDocumentTitle("Repositories");

  // Filter and order server-side. A large installation's grid is one page of
  // its repositories, and sorting a page by attention-demand in the browser
  // ranks whichever repos happened to be on it rather than the workspace.
  const filtering = team !== ALL_TEAMS;
  const {
    error,
    loading,
    refetch,
    repositories: repos,
  } = useRepositories({
    orgSlug: filtering ? team : null,
    sort,
    workspaceId,
  });

  // The team menu is a facet over the whole workspace. Unfiltered, the grid's
  // own list is exactly that; filtered, it is one org's worth, so the menu is
  // read separately rather than from a remembered copy of an earlier response —
  // otherwise picking a team erases every other team from the menu that would
  // let you leave it.
  const { repositories: everyRepo } = useRepositories({
    enabled: filtering,
    workspaceId,
  });
  const teams = [
    ...new Set((filtering ? everyRepo : repos).map((r) => r.org)),
  ].sort((a, b) => a.localeCompare(b));

  const header = (
    <div className={styles.header}>
      <h1 className={styles.title}>Repositories</h1>
      <div className={styles.controls}>
        <Menu>
          <MenuTrigger asChild>
            <Button size="sm">
              <Icon.Filter size={13} />{" "}
              {team === ALL_TEAMS ? "All teams" : team}
              <Icon.ChevDown size={12} style={{ opacity: 0.5 }} />
            </Button>
          </MenuTrigger>
          <MenuContent>
            {[ALL_TEAMS, ...teams].map((t) => (
              <MenuItem active={t === team} key={t} onSelect={() => setTeam(t)}>
                <span style={{ fontSize: 13 }}>
                  {t === ALL_TEAMS ? "All teams" : t}
                </span>
                {t === team && <MenuCheck />}
              </MenuItem>
            ))}
          </MenuContent>
        </Menu>
        <Segmented
          onValueChange={(v) => setSort(v as "activity" | "name")}
          options={[
            { label: "Most active", value: "activity" },
            { label: "A–Z", value: "name" },
          ]}
          value={sort}
        />
        <ButtonLink size="sm" to={paths.settings()} variant="accent">
          <Icon.Plus size={13} /> Connect repository
        </ButtonLink>
      </div>
    </div>
  );

  return (
    <div className={styles.page}>
      {header}
      {loading && repos.length === 0 ? (
        <RepoGridSkeleton />
      ) : error ? (
        <EmptyState
          action={
            <Button onClick={refetch} size="sm">
              <Icon.Refresh size={13} /> Try again
            </Button>
          }
          body="We couldn't reach the server to load your repositories."
          title="Something went sideways."
        />
      ) : repos.length === 0 ? (
        <EmptyState
          action={
            <ButtonLink size="sm" to={paths.settings()} variant="accent">
              <Icon.Plus size={13} /> Connect a repository
            </ButtonLink>
          }
          body="Connect GitHub in Settings and link a repository, and Tinsmith's agents can start working it."
          title="No repositories yet."
        />
      ) : (
        <div className={styles.grid}>
          {repos.map((r) => {
            const needsInput = r.agents.needsInput;
            return (
              <Link className={styles.card} key={r.id} to={paths.repo(r.id)}>
                <div className={styles.cardHead}>
                  <RepoAvatar org={r.org} />
                  <div style={{ minWidth: 0 }}>
                    <div className={styles.name}>{r.name}</div>
                    <div className={styles.org}>{r.org}</div>
                  </div>
                  <span className={styles.spacer} />
                  {needsInput > 0 ? (
                    <Pill status="needs-input">{needsInput} needs you</Pill>
                  ) : r.agents.running > 0 ? (
                    <Pill status="running">{r.agents.running} live</Pill>
                  ) : (
                    <Pill dotColor="var(--ink-4)">idle</Pill>
                  )}
                </div>
                <div className={styles.stats}>
                  <div
                    className={cx(
                      styles.statPill,
                      r.agents.running > 0 && styles.emph,
                    )}
                  >
                    <div className={styles.num}>{r.agents.running}</div>
                    <div className={styles.lbl}>Running</div>
                  </div>
                  <div className={styles.statPill}>
                    <div className={styles.num}>{r.agents.review}</div>
                    <div className={styles.lbl}>Review</div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

const RepoGridSkeleton = () => (
  <div className={styles.grid}>
    {[0, 1, 2, 3, 4, 5].map((i) => (
      <div className={cx(styles.card, styles.skeleton)} key={i}>
        <div className={styles.cardHead}>
          <div className={styles.skelLine} style={{ height: 32, width: 32 }} />
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div
              className={styles.skelLine}
              style={{ height: 12, width: 120 }}
            />
            <div
              className={styles.skelLine}
              style={{ height: 10, width: 70 }}
            />
          </div>
        </div>
        <div className={styles.stats}>
          <div className={styles.skelLine} style={{ flex: 1, height: 52 }} />
          <div className={styles.skelLine} style={{ flex: 1, height: 52 }} />
        </div>
      </div>
    ))}
  </div>
);
